import React, {
    FC,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react';

import MessageCloneForm from '../../components/formulas/MessageCloneForm';
import BasicModal from '../../components/modals/basicModal';
// components
import DropdownMenu, {
    OptionModel,
} from '../../components/nav/DrowpdownMenu/DropdownMenu';
import styled from 'styled-components';

import {
    CONTROLLED_FORMULA_BUFFER,
    CONTROLLED_FORMULA_CANCEL_BUFFER,
    CONTROLLED_FORMULA_EXCEED_BUFFER_TIME_TOOLTIP,
    CONTROLLED_FORMULA_SCHEDULED_DATE_PASS_TOOLTIP,
} from '../../constants/formula';
import { FormulaType } from '../../constants/formulaType';
// constants
import routes from '../../constants/routes';

// contexts
import { EditorContext } from '../../context/EditorContext';

// helpers
import confirm from '../../helpers/confirm';
import { hasScheduleTimeExceededBufferTime } from '../../helpers/formulaHelpers';
import { isScheduleTimeInPast } from '../../helpers/formulaHelpersPure';

import useFormulaApprove from '../../hooks/formulas/useFormulaApprove';
import useFormulaArchive from '../../hooks/formulas/useFormulaArchive';
// hooks
import useFormulaClone from '../../hooks/formulas/useFormulaClone';
import useFormulaDelete from '../../hooks/formulas/useFormulaDelete';
import useFormulaDraft from '../../hooks/formulas/useFormulaDraft';
import useFormulaReview from '../../hooks/formulas/useFormulaReview';
import useAnalytics from '../../hooks/helper/useAnalytics';
import useHandleIntegrationRequest from '../../hooks/helper/useHandleIntegrationRequest';
import { useAuthSession } from '../../hooks/useAuthSession';
import useGoBack from '../../hooks/useGoBack';

// models
import {
    ApprovedFormulaState,
    FormulaCloneOptions,
    FormulaStatus,
    FormulaTypesData,
} from '../../models/formula.model';

import FormulaEditModal from '../formulaView/FormulaEditModal';
import ApprovedSuccessModal from './formulaApprove/ApprovedSuccessModal';
import TestFormulaModal from './formulaEditor/assistant/testFormula/TestFormulaModal';

interface FormulaActionDropdownProps {
    isApproveActionDisabledProp: boolean;
    approveButtonTooltipText?: string;
    setIsEditing?: (value: boolean) => void;
    hasWriteAccess: boolean;
    hasCreateFormulaAccess: boolean;
    hasDeleteAccess: boolean;
}

const FormulaActionDropdown: FC<
    React.PropsWithChildren<FormulaActionDropdownProps>
> = ({
    isApproveActionDisabledProp,
    approveButtonTooltipText,
    setIsEditing,
    hasCreateFormulaAccess,
    hasDeleteAccess,
    hasWriteAccess,
}) => {
    const [isCloningFormula, setIsCloningFormula] = useState<boolean>(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
    const [showApproveSuccessModal, setShowApproveSuccessModal] =
        useState<boolean>(false);
    const [showSendTestMessageModal, setShowSendTestMessageModal] =
        useState(false);
    const {
        editorDetails: { previews, formula, formulaType },
    } = useContext(EditorContext);
    const { user: currentUser } = useAuthSession();

    const customerId = currentUser?.customerId;
    const { track } = useAnalytics();
    const cloneFormula = useFormulaClone();
    const draftFormula = useFormulaDraft(formula?.id as number);
    const deleteFormula = useFormulaDelete();
    const archiveFormula = useFormulaArchive();
    const reviewFormula = useFormulaReview(formula?.id.toString() as string);
    const messageApprove = useFormulaApprove(formula?.id);

    const isControlledFormula = !!formula?.is_controlled_message;

    const { handleIntegrationRequest } = useHandleIntegrationRequest(
        formulaType as FormulaType,
    );

    const isChannelIntegrated = useMemo<boolean>(
        () =>
            !!formulaType &&
            !!FormulaTypesData[formulaType].isIntegrated?.(customerId),
        [formulaType, customerId],
    );

    const isScheduledTimeInPast = useMemo(() => {
        return (
            isControlledFormula &&
            isScheduleTimeInPast(
                formula?.send_start as string,
                CONTROLLED_FORMULA_BUFFER,
            )
        );
    }, [formula, isControlledFormula]);

    const openSendTestMessageModal = () => setShowSendTestMessageModal(true);
    const closeSendTestMessageModal = () => setShowSendTestMessageModal(false);

    const openEditModalHandler = () => setIsEditModalOpen(true);

    const closeEditModalHandler = () => setIsEditModalOpen(false);

    const editLiveMessageHandler = useCallback(() => {
        draftFormula.mutate({ is_in_edit_mode: true });
        closeEditModalHandler();
    }, [draftFormula]);

    const { back } = useGoBack(routes.formulasMain, 'Formulas');

    const isDraftFormula = useMemo<boolean>(
        () => formula?.active_state === FormulaStatus.Draft,
        [formula],
    );

    const isReviewFormula = useMemo<boolean>(
        () => formula?.active_state === FormulaStatus.Review,
        [formula],
    );

    const isScheduledOrLiveFormula = useMemo<boolean>(
        () =>
            [
                ApprovedFormulaState.Scheduled,
                ApprovedFormulaState.Live,
            ].includes(formula?.active_state as ApprovedFormulaState),
        [formula],
    );

    const isLiveFormula = useMemo<boolean>(
        () =>
            ApprovedFormulaState.Live ===
            (formula?.active_state as ApprovedFormulaState),
        [formula],
    );

    const isExpiredFormula = useMemo<boolean>(
        () =>
            [ApprovedFormulaState.Expired].includes(
                formula?.active_state as ApprovedFormulaState,
            ),
        [formula],
    );

    const isArchivedFormula = useMemo<boolean>(
        () => formula?.active_state === FormulaStatus.Archived,
        [formula],
    );

    const trackEdit = useCallback(() => {
        track('formula_moved_to_draft_button_clicked', {
            id: formula?.id,
        });
    }, [formula, track]);

    const cloneFormulaHandler = useCallback(
        (data: { options: FormulaCloneOptions[] }) => {
            cloneFormula.mutate({
                formulaId: formula?.id as number,
                data: {
                    clone_options: data.options,
                },
            });
        },
        [formula, cloneFormula],
    );

    const moveToDraftFinalWarning = useMemo<string>(() => {
        let finalWarning = 'This will return your message to DRAFT mode.';
        if (isControlledFormula && isScheduledOrLiveFormula) {
            finalWarning =
                'This will cancel your scheduled message and move it to draft mode';
        }
        return finalWarning;
    }, [isControlledFormula, isScheduledOrLiveFormula]);

    const moveToDraftHandler = useCallback(async () => {
        const confirmed = await confirm(moveToDraftFinalWarning);
        if (formula?.id && confirmed) {
            trackEdit();
            draftFormula.mutate({});
        }
    }, [draftFormula, formula, moveToDraftFinalWarning, trackEdit]);

    const handleDeleteFormula = useCallback(async () => {
        const confirmed = await confirm(
            'Are you sure you want to delete this message?',
            'warning',
            undefined,
            undefined,
            'Delete message',
        );
        if (confirmed) {
            deleteFormula.mutate(formula?.id as number, {
                onSuccess: () => {
                    setTimeout(deleteFormula.reset, 2000);
                    back();
                },
            });
        }
    }, [deleteFormula, formula, back]);

    const handleArchive = useCallback(async () => {
        const confirmed = await confirm(
            'Are you sure you want to archive this message?',
            undefined,
            undefined,
            undefined,
            'Archive',
        );
        if (formula?.id && confirmed) {
            archiveFormula.mutate(formula.id, {
                onError: () => {
                    alert('Unable to archive');
                },
            });
        }
    }, [archiveFormula, formula]);

    const handleReview = useCallback(() => {
        if (formula?.id) {
            reviewFormula.mutate(formula?.id);
        }
    }, [formula, reviewFormula]);

    const handleApprove = useCallback(() => {
        if (isChannelIntegrated && formula?.id) {
            messageApprove.mutate(formula?.id, {
                onSuccess: () => {
                    setShowApproveSuccessModal(true);
                },
            });
        } else {
            handleIntegrationRequest?.();
        }
    }, [
        formula,
        messageApprove,
        isChannelIntegrated,
        handleIntegrationRequest,
    ]);

    const isEditControlledFormulaDisabled = useMemo<boolean>(
        () =>
            !!(
                isControlledFormula &&
                isScheduleTimeInPast(
                    formula?.send_start as string,
                    CONTROLLED_FORMULA_BUFFER,
                )
            ),
        [isControlledFormula, formula],
    );

    const isScheduleTimeExceededBufferTime = useMemo<boolean>(() => {
        if (isControlledFormula && isLiveFormula) {
            return hasScheduleTimeExceededBufferTime(
                formula?.send_start as string,
                CONTROLLED_FORMULA_CANCEL_BUFFER,
            );
        }
        return false;
    }, [isControlledFormula, isLiveFormula, formula]);

    const moveToDraftTooltipText = useMemo<string | undefined>(() => {
        if (!isReviewFormula) {
            return isEditControlledFormulaDisabled
                ? CONTROLLED_FORMULA_SCHEDULED_DATE_PASS_TOOLTIP
                : 'Change the content and settings of this message.';
        }
        return undefined;
    }, [isEditControlledFormulaDisabled, isReviewFormula]);

    const archiveFormulaTooltipText = useMemo(() => {
        return isScheduleTimeExceededBufferTime
            ? CONTROLLED_FORMULA_EXCEED_BUFFER_TIME_TOOLTIP
            : 'Pressing this button will stop sending all of the variants of this message group and move them to the archived catalog.';
    }, [isScheduleTimeExceededBufferTime]);

    const isApproveActionDisabled = useMemo<boolean>(() => {
        return (
            isScheduledTimeInPast ||
            !previews?.length ||
            isApproveActionDisabledProp
        );
    }, [isScheduledTimeInPast, isApproveActionDisabledProp, previews]);

    const approveActionTooltipText = useMemo<string>(() => {
        return isScheduledTimeInPast
            ? `Controlled message can only be scheduled at least ${CONTROLLED_FORMULA_BUFFER} minutes in future.`
            : approveButtonTooltipText ||
                  'Pressing this button will schedule this Message Group to start sending (within the audience, trigger, and time constraints you’ve set) to eligible users.';
    }, [approveButtonTooltipText, isScheduledTimeInPast]);

    const isReviewActionDisabled = useMemo<boolean>(() => {
        return !previews?.length || reviewFormula.isPending;
    }, [previews, reviewFormula]);

    const finalOptions = useMemo<OptionModel[]>(() => {
        const moveToDraftActionOption = {
            name: 'Move to draft',
            action: moveToDraftHandler,
            isDisabled: isEditControlledFormulaDisabled,
            tooltipText: moveToDraftTooltipText,
        };
        const deleteActionOption = {
            name: 'Delete',
            action: handleDeleteFormula,
        };
        const archiveActionOption = {
            name: 'Archive',
            action: handleArchive,
            isDisabled: isScheduleTimeExceededBufferTime,
            tooltipText: archiveFormulaTooltipText,
        };
        const cloneActionOption = {
            name: 'Clone',
            action: () => setIsCloningFormula(true),
        };
        const requestForReviewOption = {
            name: 'Request for review',
            action: handleReview,
            isDisabled: isReviewActionDisabled,
        };
        const approveOption = {
            name: 'Approve',
            action: handleApprove,
            isDisabled: isApproveActionDisabled,
            tooltipText: approveActionTooltipText,
        };
        const editOption = {
            name: 'Edit',
            action: openEditModalHandler,
        };

        const sendTestMessageOption = {
            name: 'Send a test message',
            action: openSendTestMessageModal,
        };

        let options: OptionModel[] = [];
        if (isDraftFormula) {
            options = [
                ...(isControlledFormula
                    ? []
                    : hasWriteAccess
                    ? [requestForReviewOption]
                    : []),
                ...(hasCreateFormulaAccess ? [cloneActionOption] : []),
                ...(hasDeleteAccess ? [deleteActionOption] : []),
            ];
        }
        if (isArchivedFormula) {
            options = [
                ...(hasCreateFormulaAccess ? [cloneActionOption] : []),
                ...(hasWriteAccess ? [moveToDraftActionOption] : []),
            ];
        }
        if (isReviewFormula) {
            options = [
                ...(hasWriteAccess ? [approveOption] : []),
                ...(hasWriteAccess ? [editOption] : []),
                ...(hasWriteAccess ? [moveToDraftActionOption] : []),
                ...(hasCreateFormulaAccess ? [cloneActionOption] : []),
                ...(hasDeleteAccess ? [deleteActionOption] : []),
            ];
        }
        if (isScheduledOrLiveFormula) {
            options = [
                sendTestMessageOption,
                ...(hasWriteAccess ? [moveToDraftActionOption] : []),
                ...(hasCreateFormulaAccess ? [cloneActionOption] : []),
                ...(hasWriteAccess ? [archiveActionOption] : []),
            ];
        }
        if (isExpiredFormula) {
            options = [
                ...(hasWriteAccess ? [moveToDraftActionOption] : []),
                ...(hasCreateFormulaAccess ? [cloneActionOption] : []),
            ];
        }
        return options;
    }, [
        isDraftFormula,
        isReviewFormula,
        isScheduledOrLiveFormula,
        isExpiredFormula,
        isArchivedFormula,
        handleDeleteFormula,
        moveToDraftHandler,
        handleArchive,
        isEditControlledFormulaDisabled,
        moveToDraftTooltipText,
        isScheduleTimeExceededBufferTime,
        archiveFormulaTooltipText,
        isApproveActionDisabled,
        approveActionTooltipText,
        handleApprove,
        hasDeleteAccess,
        hasCreateFormulaAccess,
        hasWriteAccess,
        handleReview,
        isControlledFormula,
        isReviewActionDisabled,
    ]);

    useEffect(() => {
        if (!cloneFormula.isPending && cloneFormula.isError) {
            cloneFormula.reset();
            setIsCloningFormula(false);
        }
    }, [cloneFormula]);

    return (
        <Container>
            <DropdownMenu options={finalOptions} isVertical />
            {isCloningFormula && (
                <BasicModal width={'450px'}>
                    <MessageCloneForm
                        onSubmit={cloneFormulaHandler}
                        onCancel={() => setIsCloningFormula(false)}
                        isSubmitting={cloneFormula.isPending}
                    />
                </BasicModal>
            )}
            {isEditModalOpen && (
                <FormulaEditModal
                    onClose={closeEditModalHandler}
                    onEditLiveMessage={editLiveMessageHandler}
                    onEditMetadata={() => {
                        setIsEditing?.(true);
                        closeEditModalHandler();
                    }}
                />
            )}
            {showApproveSuccessModal && (
                <ApprovedSuccessModal
                    handleClose={setShowApproveSuccessModal}
                />
            )}
            {showSendTestMessageModal && (
                <TestFormulaModal handleClose={closeSendTestMessageModal} />
            )}
        </Container>
    );
};

const Container = styled.div(() => ({
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
}));

export default FormulaActionDropdown;
